import { Select, Spin } from "antd";
import { useState, useCallback } from "react";
import debounce from "lodash/debounce";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { Col, Label, Modal, ModalBody, ModalHeader, Row } from "reactstrap";
import { getAllProducts, MergeProductsApi } from "../../api/pagination";
import GeneralButton from "../button/GeneralButton";

import { Card, CardBody } from "reactstrap";

const SelectedProductInfo = ({ product }) => {
  if (!product) return null;

  const infoBoxes = [
    { label: "Selected", value: product.sku },
    { label: "QB ID", value: product.quickbooks_id },
    { label: "Identifiers", value: product.identifiers && product.identifiers.length > 0 ? product.identifiers.join(", ").toUpperCase() : "TECHMICRO" },
    { label: "System ID", value: product.id },
    { label: "Stock", value: product.stc_quantity },
  ];

  return (
    <div className="mt-3">
      <Row className="g-2">
        {infoBoxes.map((box, index) => (
          <Col md="3" sm="6" xs="12" key={index}>
            <Card className=" shadow-sm rounded-3 ">
              <CardBody className="py-2 px-3 text-center">
                <div className="fw-bold mb-1" style={{ color: "var(--primary-blue)" }}>{box.label}</div>
                <div className="text-muted fs-14">{box.value || "—"}</div>
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
};

const MergingProducts = ({ modal, toggle }) => {
  const { all_products } = useSelector((state) => state.products);
  const [searchProducts, setSearchProducts] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [merging, setMerging] = useState(false);

  const [fromProduct, setFromProduct] = useState(null);
  const [toProduct, setToProduct] = useState(null);
  const [confirmMerge, setConfirmMerge] = useState(false);

  const fetchProductsDebounced = useCallback(
    debounce((searchText) => {
      getAllProducts(1, searchText, 50, "", (res) => {
        if (res) {
          setSearchProducts(res?.data?.data || []);
        }
        setDataLoading(false);
      });
    }, 500),
    []
  );

  // Fetch products for search
  const handleSearchProducts = (searchText) => {
    setDataLoading(true);
    fetchProductsDebounced(searchText);
  };

  // Handle selecting product
  const handleSelectProduct = (value, type) => {
    const list = searchProducts?.length > 0 ? searchProducts : all_products;
    const selected = list.find((i) => i?._id === value);

    if (!selected) {
      toast.error("Selected product not found.");
      return;
    }

    if (type === "from") {
      if (toProduct && toProduct._id === selected._id) {
        toast.error("You cannot select the same product for both fields.");
        return;
      }
      setFromProduct(selected);
    } else if (type === "to") {
      if (fromProduct && fromProduct._id === selected._id) {
        toast.error("You cannot select the same product for both fields.");
        return;
      }
      setToProduct(selected);
    }

    setConfirmMerge(false); // reset checkbox when selections change
  };

  const handleMerge = async () => {
    if (!fromProduct || !toProduct) {
      toast.error("Please select both From and To products.");
      return;
    }

    setMerging(true);
    toast.loading("Merging products...");

    await MergeProductsApi(fromProduct._id, toProduct._id, (res) => {
      toast.dismiss();
      setMerging(false);

      if (res?.status === 200 && res?.data?.success) {
        const result = res.data.results || {};
        toast.success(
          `✅ ${res.data.message}\n
         🛒 Sales Orders: ${result.saleOrdersUpdated || 0}
         📦 Purchase Orders: ${result.purchaseOrdersUpdated || 0}
         📋 Quotes: ${result.quotesUpdated || 0}
         🚚 Trackings: ${result.trackingsUpdated || 0}`
        );


        // Optional: close modal and reset selections
        setFromProduct(null);
        setToProduct(null);
        setConfirmMerge(false);
        toggle();
      } else {
        toast.error(
          res?.message ||
          res?.data?.message ||
          "Something went wrong while merging products."
        );
      }
    });
  };

  return (
    <Modal isOpen={modal} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle}>
        <span style={{ color: "var(--primary-red)", fontWeight: "bold" }}>Merging Products</span>
      </ModalHeader>
      <ModalBody>
        <Row>
          {/* FROM PRODUCT */}
          <Col md={12}>
            <Label className="fw-bold">From</Label>
            <Select
              size="large"
              showSearch
              loading={dataLoading}
              notFoundContent={
                dataLoading ? <Spin size="small" /> : "No products found"
              }
              placeholder="Search Product to merge FROM"
              filterOption={false}
              onSelect={(value) => handleSelectProduct(value, "from")}
              onSearch={(val) => handleSearchProducts(val)}
              className="mb-2 w-100"
              getPopupContainer={(trigger) => trigger.parentNode}
              value={fromProduct ? fromProduct._id : undefined}
              options={(searchProducts?.length > 0
                ? searchProducts
                : all_products
              )?.map((item) => ({
                value: item._id,
                label: `${item.sku} - [${item.identifiers && item.identifiers.length > 0 ? item.identifiers.join(", ").toUpperCase() : "TECHMICRO"}]`,
              }))}
            />
            {fromProduct && <SelectedProductInfo product={fromProduct} />}
          </Col>

          {/* TO PRODUCT */}
          <Col md={12} className="mt-3">
            <Label className="fw-bold">To</Label>
            <Select
              size="large"
              showSearch
              loading={dataLoading}
              notFoundContent={
                dataLoading ? <Spin size="small" /> : "No products found"
              }
              placeholder="Search Product to merge INTO"
              filterOption={false}
              onSelect={(value) => handleSelectProduct(value, "to")}
              onSearch={(val) => handleSearchProducts(val)}
              className="mb-2 w-100"
              getPopupContainer={(trigger) => trigger.parentNode}
              value={toProduct ? toProduct._id : undefined}
              options={(searchProducts?.length > 0
                ? searchProducts
                : all_products
              )?.map((item) => ({
                value: item._id,
                label: `${item.sku} - [${item.identifiers && item.identifiers.length > 0 ? item.identifiers.join(", ").toUpperCase() : "TECHMICRO"}]`,
              }))}
            />
            {toProduct && <SelectedProductInfo product={toProduct} />}
          </Col>
        </Row>

        {/* ⚠️ Merge Warning Section */}
        {fromProduct && toProduct && (
          <div
            className="mt-4 p-3 border border-dashed rounded"
            style={{
              backgroundColor: "rgba(227, 27, 35, 0.05)",
              borderColor: "var(--primary-red)",
              color: "var(--primary-red)",
            }}
          >
            <strong>⚠️ Warning:</strong> Merging these products will
            <strong> permanently affect all related records</strong> — including
            sales orders, purchase orders, manufacturers, and reports.
            <br />
            This action <strong>cannot be undone</strong>.
            <div className="form-check mt-3">
              <input
                type="checkbox"
                id="confirmMerge"
                className="form-check-input"
                checked={confirmMerge}
                onChange={(e) => setConfirmMerge(e.target.checked)}
              />
              <label htmlFor="confirmMerge" className="form-check-label">
                I understand and confirm to proceed with the merge.
              </label>
            </div>
          </div>
        )}

        {/* Save Button */}
        <div className="d-flex justify-content-end">
          {fromProduct && toProduct && confirmMerge && (
            <GeneralButton
              title={"Merge Products"}
              className={"red-button mt-3"}
              width={"160px"}
              handleClick={handleMerge}
              disabled={merging}
            />
          )}
        </div>
      </ModalBody>
    </Modal>
  );
};

export default MergingProducts;
